import { useCallback, useMemo } from 'react';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { WatchQueryFetchPolicy } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import type { ConcreteRequest } from 'relay-runtime';

import { fg } from '@confluence/feature-gating';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import PageTreeQuery from './queries/__generated__/PageTreeQuery.graphql.ts';
import type { PageTreeQuery as PageTreeQueryResponse } from './queries/__types__/PageTreeQuery.ts';
import type { PageTreeRootLevelPagesQuery as PageTreeRootLevelPagesResponse } from './queries/__types__/PageTreeRootLevelPagesQuery.ts';
import PageTreeRootLevelQuery from './queries/__generated__/PageTreeRootLevelQuery.graphql.ts';
import { INITIAL_PAGE_SIZE } from './paginationLimits.ts';
import type { PageTreeState } from './usePageTreeState.ts';
import { usePageTreeState } from './usePageTreeState.ts';
import { flattenTreeData, deriveStateFromData } from './data-transformers.ts';
import { isFollowingNodeHidden } from './data-extractors.ts';
import { useLoadMorePages } from './useLoadMorePages.ts';
import { usePageTreeChildren } from './usePageTreeChildren.ts';
import { PAGE_TREE_STATUSES } from './pageTreeStatuses.ts';
import type { GraphQLPageStatus } from './pageTreeStatuses.ts';
export const generateQueryArgs = (
	targetPage: any,
	paginated: any,
	forceFetch: any,
	detached: any,
	spaceKey: any,
	isSuperAdmin: any,
): {
	query: ConcreteRequest;
	options: {
		fetchPolicy: string;
		variables:
			| {
					spaceKey: any;
					isSuperAdmin: any;
					statuses: GraphQLPageStatus[];
					ptStatuses: GraphQLPageStatus[];
					contentId: any;
					emojiTitleEnabled?: undefined;
			  }
			| {
					spaceKey: any;
					isSuperAdmin: any;
					statuses: GraphQLPageStatus[];
					emojiTitleEnabled: boolean;
					ptStatuses?: undefined;
					contentId?: undefined;
			  };
	};
} => {
	const query = targetPage ? PageTreeQuery : PageTreeRootLevelQuery;

	const defaultVariables = targetPage
		? {
				spaceKey,
				isSuperAdmin,
				statuses: PAGE_TREE_STATUSES,
				ptStatuses: PAGE_TREE_STATUSES,
				contentId: targetPage,
			}
		: {
				spaceKey,
				isSuperAdmin,
				statuses: PAGE_TREE_STATUSES,
				emojiTitleEnabled: true,
			};

	const variablesForPagination = {
		paginationLimit: INITIAL_PAGE_SIZE,
		...defaultVariables,
	};

	return {
		query,
		options: {
			fetchPolicy: detached ? 'no-cache' : forceFetch ? 'network-only' : 'cache-first',
			variables: paginated === true || !targetPage ? variablesForPagination : defaultVariables,
		},
	};
};

export const usePageTreeData = (
	spaceKey?: string,
	isSuperAdmin?: boolean,
	statuses?: GraphQLPageStatus[],
	onLoadMore?: (length, direction?: 'following' | 'previous') => void,
): {
	loadPreviousPages: () => void;
	loadFollowingPages: () => void;
	reloadChildren: () => void;
	queryChildren: any;
} => {
	const [state, actions] = usePageTreeState();
	const { focusedPageId } = state as PageTreeState;
	const { loadMorePages } = useLoadMorePages({ statuses });
	const { reloadChildren, queryChildren } = usePageTreeChildren({
		statuses,
		isSuperAdmin,
	});

	const loadMoreDirectionalPages = useCallback(
		async (direction: 'previous' | 'following') => {
			actions.setIsPageTreeLoading(true);

			// Edge Case: if there are hidden following ancestors, we need to load previous as well as following siblings if applicable
			const followingNodeHidden = isFollowingNodeHidden(state.initialPages);
			let pages = state.initialPages;
			if (direction === 'following' && followingNodeHidden) {
				const { mergedPages: prevPages, error: prevError } = await loadMorePages(
					focusedPageId,
					spaceKey,
					isSuperAdmin,
					state.cursors,
					pages,
					'previous',
				);

				if (prevError) {
					//toDo: handle loading previous / next errors here, then reset state so they can attempt to try again
					return;
				}

				pages = prevPages;
			}

			const { mergedPages, rootLevelPages, error } = await loadMorePages(
				focusedPageId,
				spaceKey,
				isSuperAdmin,
				state.cursors,
				pages,
				direction,
			);

			if (error) {
				//toDo: handle loading previous / next errors here, then reset state so they can attempt to try again
				return;
			}

			const newState = deriveStateFromData(
				{ currentPage: mergedPages, rootLevelPages, space: state.space },
				spaceKey,
				state,
				false,
				undefined,
				false,
			);

			actions.setStateWithUIAttributes(newState);
			const flattenedPages = flattenTreeData(mergedPages, rootLevelPages);
			actions.setIsPageTreeLoading(false);
			if (onLoadMore) {
				onLoadMore(Object.keys(flattenedPages).length, direction);
			}
		},
		[actions, loadMorePages, focusedPageId, isSuperAdmin, onLoadMore, spaceKey, state],
	);

	const loadPreviousPages = useCallback(
		() => loadMoreDirectionalPages('previous'),
		[loadMoreDirectionalPages],
	);
	const loadFollowingPages = useCallback(
		() => loadMoreDirectionalPages('following'),
		[loadMoreDirectionalPages],
	);

	return {
		loadPreviousPages,
		loadFollowingPages,
		reloadChildren,
		queryChildren,
	};
};

export const useFetchPages = (
	currentPageId: string | undefined,
	paginated: boolean,
	forceFetch: boolean,
	detached: boolean,
	spaceKey?: string,
	isSuperAdmin?: boolean,
): UseQueryInteropSupportedResult<PageTreeQueryResponse | PageTreeRootLevelPagesResponse> => {
	const { query, options } = useMemo(
		() => generateQueryArgs(currentPageId, paginated, forceFetch, detached, spaceKey, isSuperAdmin),
		[currentPageId, paginated, forceFetch, detached, spaceKey, isSuperAdmin],
	);
	return useQueryInterop<PageTreeQueryResponse | PageTreeRootLevelPagesResponse>(query, {
		skip: true,
		fetchPolicy: options.fetchPolicy as WatchQueryFetchPolicy,
		variables: options.variables,
	});
};

export const useLoadPagesCallback = (
	spaceKey: any,
	expandSpaceHomepage: any,
	syntheticItem: any,
	isPeekingFromBlogs = false,
	isSuperAdmin = false,
	statuses: any,
	syntheticItemIsDraft: any,
	pageTreeStateUpdatesContainer: any,
): ((data: any) => any) => {
	const [state, actions] = usePageTreeState();
	const { queryChildren } = usePageTreeChildren({
		statuses,
		isSuperAdmin,
	});
	return useCallback(
		(data: any) => {
			if (data && Object.keys(data).length > 0) {
				const spaceHomePageId = data.space?.homepage?.id;
				const newState = deriveStateFromData(
					data,
					spaceKey,
					state,
					isPeekingFromBlogs,
					syntheticItem,
					syntheticItemIsDraft,
					pageTreeStateUpdatesContainer,
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					fg('confluence_frontend_whiteboard_emoji_titles'),
				);

				actions.setStateWithUIAttributes(newState);
				const { rootId, pages: allPages } = newState;

				if (
					expandSpaceHomepage &&
					spaceHomePageId &&
					rootId !== spaceHomePageId &&
					allPages[spaceHomePageId]
				) {
					return actions.expandPage(allPages[spaceHomePageId], queryChildren);
				}
				return allPages;
			}
			return data;
		},
		[
			spaceKey,
			state,
			isPeekingFromBlogs,
			syntheticItem,
			syntheticItemIsDraft,
			actions,
			expandSpaceHomepage,
			queryChildren,
			pageTreeStateUpdatesContainer,
		],
	);
};
